import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle2, 
  Phone, 
  Mail, 
  Clock, 
  ShieldCheck, 
  Layers, 
  Compass, 
  Sparkles,
  Plus,
  Minus,
  HelpCircle
} from "lucide-react";
import FadeIn from "../components/FadeIn";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import Parallax from "../components/Parallax";
import { SERVICES_DATA, CONTACT_INFO } from "../data";

// Helper to format Cloudflare image URLs
function getOptimizedImage(url, width = 1400) {
  if (!url || !url.includes("assets.auxodev.dk")) return url;
  if (url.includes("/cdn-cgi/image/")) return url;
  return url.replace(
    "https://assets.auxodev.dk/",
    `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`
  );
}

export default function ServiceDetail({ fixedSlug }) {
  const params = useParams();
  const rawSlug = fixedSlug || params.slug;
  const navigate = useNavigate();
  const { lang, t } = useLanguage();

  // Normalize slug for matching
  const currentSlug = (rawSlug || "").toLowerCase().trim();

  // Find matching service
  const serviceIndex = SERVICES_DATA.findIndex((s) => {
    return (
      s.id.toLowerCase() === currentSlug ||
      (s.slug?.da && s.slug.da.toLowerCase() === currentSlug) ||
      (s.slug?.en && s.slug.en.toLowerCase() === currentSlug) ||
      // Aliases
      (currentSlug === "turnkey" && s.id === "turnkey") ||
      (currentSlug === "totalentreprise" && s.id === "turnkey") ||
      (currentSlug === "projektering-af-byggeri" && s.id === "engineering") ||
      (currentSlug === "building-engineering" && s.id === "engineering") ||
      (currentSlug === "toemrerarbejde" && s.id === "carpentry") ||
      (currentSlug === "carpentry" && s.id === "carpentry") ||
      (currentSlug === "renovering" && s.id === "renovation") ||
      (currentSlug === "renovation" && s.id === "renovation") ||
      (currentSlug === "byggepladsservice" && s.id === "site-services") ||
      (currentSlug === "construction-site-services" && s.id === "site-services") ||
      (currentSlug === "site-services" && s.id === "site-services") ||
      (currentSlug === "specialprojekter" && s.id === "custom-projects") ||
      (currentSlug === "custom-projects" && s.id === "custom-projects") ||
      (currentSlug === "totalrenovering" && s.id === "totalrenovation") ||
      (currentSlug === "total-renovation" && s.id === "totalrenovation") ||
      (currentSlug === "byggeprojekt" && s.id === "byggeprojekt") ||
      (currentSlug === "construction-projects" && s.id === "byggeprojekt") ||
      (currentSlug === "haandvaerker-byggeprojekt" && s.id === "haandvaerker-byggeprojekt") ||
      (currentSlug === "haandvaerker" && s.id === "haandvaerker-byggeprojekt") ||
      (currentSlug === "trades-construction-projects" && s.id === "haandvaerker-byggeprojekt") ||
      (currentSlug === "trades" && s.id === "haandvaerker-byggeprojekt")
    );
  });

  const service = serviceIndex !== -1 ? SERVICES_DATA[serviceIndex] : null;

  // Next & previous service calculation
  const prevService = serviceIndex > 0 ? SERVICES_DATA[serviceIndex - 1] : SERVICES_DATA[SERVICES_DATA.length - 1];
  const nextService = serviceIndex < SERVICES_DATA.length - 1 ? SERVICES_DATA[serviceIndex + 1] : SERVICES_DATA[0];

  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setOpenFaqIndex(0);
  }, [currentSlug]);

  if (!service) {
    return (
      <div className="bg-stone-50 text-stone-900 min-h-screen pt-32 pb-24 px-6 md:px-12 flex flex-col items-center justify-center">
        <h1 className="text-3xl md:text-5xl font-light uppercase tracking-tight mb-4">
          {lang === "da" ? "Ydelsen blev ikke fundet" : "Service not found"}
        </h1>
        <p className="text-stone-600 mb-8 font-light">
          {lang === "da" 
            ? "Den efterspurgte ydelse findes ikke eller er flyttet." 
            : "The requested service does not exist or has been relocated."}
        </p>
        <Link
          to={lang === "da" ? "/ydelser" : "/en/services"}
          className="inline-flex items-center gap-2 px-6 py-3 bg-stone-900 text-white text-xs uppercase tracking-widest font-mono"
        >
          <ArrowLeft className="w-4 h-4" />
          {lang === "da" ? "Se alle ydelser" : "View all services"}
        </Link>
      </div>
    );
  }

  const title = service.title[lang] || service.title.da;
  const heroHeading = service.heroHeading?.[lang] || service.heroHeading?.da || title;
  const subtitle = service.subtitle[lang] || service.subtitle.da;
  const description = service.description[lang] || service.description.da;
  const longParagraphs = service.longDescription?.[lang] || [description];
  const includes = service.includes?.[lang] || [];
  const processSteps = service.process || [];
  const benefits = service.benefits || [];
  const idealFor = service.idealFor?.[lang] || "";
  const faqs = service.faq || [];
  const seoKeywords = service.seoKeywords?.[lang] || [];
  const seoTitle = service.seoTitle?.[lang] || `${title} | CPH Collective`;
  const seoDesc = service.seoDescription?.[lang] || description;
  const seoImage = service.id === "renovation"
    ? "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-arkitektur-totalentreprise-koebenhavn.jpg"
    : service.image;

  const servicesPath = lang === "da" ? "/ydelser" : "/en/services";
  const contactPath = lang === "da" ? "/kontakt" : "/en/contact";
  const homePath = lang === "da" ? "/" : "/en";
  const prevSlug = prevService.slug?.[lang] || prevService.id;
  const nextSlug = nextService.slug?.[lang] || nextService.id;
  const prevPath = lang === "da" ? `/ydelser/${prevSlug}` : `/en/services/${prevSlug}`;
  const nextPath = lang === "da" ? `/ydelser/${nextSlug}` : `/en/services/${nextSlug}`;

  // Tracker so only the very first mention of CPH Collective links to the homepage
  let hasLinkedHome = false;

  const linkFirstCphCollective = (text, isDark = false) => {
    if (!text || typeof text !== "string") return text;
    if (hasLinkedHome || !text.includes("CPH Collective")) return text;

    const parts = text.split("CPH Collective");
    hasLinkedHome = true;
    const linkStyle = isDark
      ? "text-white font-medium underline underline-offset-4 decoration-stone-400 hover:decoration-white hover:text-stone-100 transition-colors"
      : "text-stone-900 font-medium underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 hover:text-stone-950 transition-colors";

    return (
      <>
        {parts[0]}
        <Link to={homePath} className={linkStyle}>
          CPH Collective
        </Link>
        {parts.slice(1).join("CPH Collective")}
      </>
    );
  };

  // Helper to render text with rich internal links
  const renderRichText = (textItem, isDark = false) => {
    if (!textItem) return null;
    const rawString = typeof textItem === "object" ? textItem[lang] || textItem.da : textItem;
    if (!rawString) return null;

    const linkClass = isDark
      ? "text-white font-medium underline underline-offset-4 decoration-stone-400 hover:decoration-white hover:text-stone-100 transition-colors"
      : "text-stone-900 font-normal underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 hover:text-stone-950 transition-colors";

    // Pattern: {link:KEY}
    const parts = rawString.split(/(\{link:[^}]+\})/g);
    return parts.map((part, idx) => {
      if (part === "{link:totalentreprise}") {
        return (
          <Link
            key={idx}
            to={lang === "da" ? "/totalentreprise" : "/en/services/turnkey"}
            className={linkClass}
          >
            {lang === "da" ? "totalentreprise" : "turnkey contract"}
          </Link>
        );
      }
      if (part === "{link:projektering}") {
        return (
          <Link
            key={idx}
            to={lang === "da" ? "/projektering-af-byggeri" : "/en/services/building-engineering"}
            className={linkClass}
          >
            {lang === "da" ? "projektering af byggeri" : "building engineering"}
          </Link>
        );
      }
      if (part === "{link:toemrerarbejde}") {
        return (
          <Link
            key={idx}
            to={lang === "da" ? "/toemrerarbejde" : "/en/services/carpentry"}
            className={linkClass}
          >
            {lang === "da" ? "tømrerarbejde" : "carpentry"}
          </Link>
        );
      }
      if (part === "{link:byggepladsservice}") {
        return (
          <Link
            key={idx}
            to={lang === "da" ? "/byggepladsservice" : "/en/services/construction-site-services"}
            className={linkClass}
          >
            {lang === "da" ? "byggepladsservice" : "construction site services"}
          </Link>
        );
      }
      if (part === "{link:renovering}") {
        return (
          <Link
            key={idx}
            to={lang === "da" ? "/renovering" : "/en/services/renovation"}
            className={linkClass}
          >
            {lang === "da" ? "renovering" : "renovation"}
          </Link>
        );
      }
      if (part === "{link:totalrenovering}") {
        return (
          <Link
            key={idx}
            to={lang === "da" ? "/totalrenovering" : "/en/services/total-renovation"}
            className={linkClass}
          >
            {lang === "da" ? "totalrenovering" : "total renovation"}
          </Link>
        );
      }
      if (part === "{link:projekter}") {
        return (
          <Link
            key={idx}
            to={lang === "da" ? "/projekter" : "/en/projects"}
            className={linkClass}
          >
            {lang === "da" ? "vores projekter" : "our projects"}
          </Link>
        );
      }
      if (part === "{link:kontakt}") {
        return (
          <Link
            key={idx}
            to={contactPath}
            className={linkClass}
          >
            {lang === "da" ? "Kontakt CPH Collective" : "Contact CPH Collective"}
          </Link>
        );
      }
      if (part === "{link:kontakt-os}") {
        return (
          <Link
            key={idx}
            to={contactPath}
            className={linkClass}
          >
            {lang === "da" ? "Kontakt os" : "Contact us"}
          </Link>
        );
      }
      if (part === "{link:byggeprojekt}") {
        return (
          <Link
            key={idx}
            to={lang === "da" ? "/byggeprojekt" : "/en/construction-projects"}
            className={linkClass}
          >
            {lang === "da" ? "byggeprojekt" : "construction project"}
          </Link>
        );
      }
      if (part === "{link:haandvaerker}") {
        return (
          <Link
            key={idx}
            to={lang === "da" ? "/haandvaerker-byggeprojekt" : "/en/services/trades-construction-projects"}
            className={linkClass}
          >
            {lang === "da" ? "håndværker til byggeprojekt" : "craftsmen for building projects"}
          </Link>
        );
      }
      return linkFirstCphCollective(part, isDark);
    });
  };

  return (
    <div className="bg-stone-50 text-stone-900 min-h-screen pt-24 md:pt-28">
      {/* Dynamic SEO Meta */}
      <SEO title={seoTitle} description={seoDesc} image={seoImage} />

      {/* Sub-header Navigation & Breadcrumb */}
      <div className="w-full px-6 md:px-12 lg:px-16 py-4 border-b border-stone-200 bg-stone-100/60 backdrop-blur-xs">
        <div className="flex flex-wrap items-center justify-between gap-4 text-xs font-mono">
          <Link
            to={servicesPath}
            className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-950 uppercase tracking-[0.2em] transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>{lang === "da" ? "Alle Ydelser" : "All Services"}</span>
          </Link>

          <div className="flex items-center gap-2 text-stone-400">
            <span className="hidden sm:inline">{lang === "da" ? "Ydelser" : "Services"}</span>
            <span className="hidden sm:inline">/</span>
            <span className="text-stone-900 font-semibold uppercase tracking-wider">{title}</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-12 md:py-16 lg:py-20 border-b border-stone-200 bg-stone-50/40">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-start">
            {/* Left: Heading & Intro */}
            <div className="lg:col-span-7">
              <FadeIn direction="up">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-2.5 h-2.5 rounded-full bg-stone-900 inline-block" />
                  <span className="text-xs uppercase tracking-[0.35em] text-stone-500 font-mono">
                    0{serviceIndex + 1} / {lang === "da" ? "Faglig Ydelse" : "Core Service"}
                  </span>
                </div>

                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] font-light tracking-tight text-stone-900 uppercase leading-[1.12] mb-6 max-w-3xl">
                  {heroHeading}
                </h1>

                <p className="text-base sm:text-lg md:text-xl font-light text-stone-600 leading-relaxed max-w-2xl mb-8">
                  {subtitle}
                </p>

                {/* Direct Action Buttons */}
                <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-stone-200/80">
                  <Link
                    to={`${contactPath}?service=${encodeURIComponent(title)}`}
                    className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-white bg-stone-900 hover:bg-stone-800 transition-all duration-300 px-8 py-4 font-semibold font-mono"
                  >
                    <span>{lang === "da" ? "Indhent tilbud" : "Request a quote"}</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a
                    href={`tel:${CONTACT_INFO.phone.replace(/\s+/g, "")}`}
                    className="inline-flex items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-stone-700 hover:text-stone-950 bg-stone-100 hover:bg-stone-200 border border-stone-200 transition-all px-6 py-4 font-mono"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    <span>{CONTACT_INFO.phone}</span>
                  </a>
                </div>
              </FadeIn>
            </div>

            {/* Right: Key Facts Card */}
            <div className="lg:col-span-5">
              <FadeIn direction="up" delay={150}>
                <div className="p-8 md:p-10 bg-stone-900 text-stone-100 border border-stone-800 space-y-6">
                  <div className="flex items-center justify-between border-b border-stone-800 pb-4">
                    <span className="text-xs uppercase tracking-[0.25em] text-stone-400 font-mono">
                      {lang === "da" ? "Hovedfokus" : "Primary Focus"}
                    </span>
                    <ShieldCheck className="w-4 h-4 text-stone-300" />
                  </div>

                  <p className="text-stone-300 text-sm md:text-base font-light leading-relaxed">
                    {linkFirstCphCollective(description, true)}
                  </p>

                  <div className="border-t border-stone-800 pt-5 space-y-3 text-xs font-mono text-stone-400">
                    <div className="flex items-center justify-between">
                      <span>{lang === "da" ? "Geografisk dækning:" : "Territory:"}</span>
                      <span className="text-stone-200 font-normal">København & Sjælland</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>{lang === "da" ? "Aftaleform:" : "Contract form:"}</span>
                      <span className="text-stone-200 font-normal">Fast tilbud / Rammeaftale</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>{lang === "da" ? "Kvalitetssikring:" : "Quality control:"}</span>
                      <span className="text-stone-200 font-normal">Fuld KS-dokumentation</span>
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Main Image Feature */}
      <section className="border-b border-stone-200 bg-stone-100">
        <div className="w-full">
          <div className="relative h-[420px] md:h-[620px] w-full overflow-hidden">
            <Parallax speed={-0.08} type="background" className="w-full h-full">
              <img
                src={getOptimizedImage(service.image, 1800)}
                alt={`${title} – Totalentreprise & Tømrerarbejde | CPH Collective`}
                className="w-full h-full object-cover object-center"
              />
            </Parallax>
            <div className="absolute inset-0 bg-stone-950/25 pointer-events-none" />
            <div className="absolute bottom-6 left-6 md:bottom-12 md:left-12 z-20">
              <span className="text-xs uppercase tracking-[0.3em] font-mono text-white/90 bg-stone-950/60 backdrop-blur-md px-4 py-2 border border-white/20">
                CPH Collective · {title}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* In-depth Editorial Content & Philosophy */}
      <section className="py-24 md:py-32 border-b border-stone-200">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24 items-start">
            {/* Left: Detailed Text */}
            <div className="lg:col-span-7 space-y-8">
              <FadeIn direction="up">
                <span className="text-xs uppercase tracking-[0.35em] text-stone-400 font-mono block mb-3">
                  {lang === "da" ? "Faglig Dybde & Tilgang" : "Professional Approach"}
                </span>

                {service.customEditorial ? (
                  <div className="space-y-16">
                    {service.customEditorial.sections.map((section, sIdx) => {
                      if (section.type === "dual-cards") {
                        return (
                          <div key={section.id || sIdx} className="space-y-4 my-10">
                            <div className="flex items-center gap-2 text-stone-400 font-mono text-xs uppercase tracking-[0.3em]">
                              <span className="w-1.5 h-1.5 rounded-full bg-stone-900" />
                              <span>{section.tag?.[lang] || section.tag?.da || (lang === "da" ? "02 / Faseopdelt Forløb & Koordinering" : "02 / Phased Execution & Coordination")}</span>
                            </div>
                            {section.title && (
                              <h2 className="text-2xl md:text-3xl font-light uppercase tracking-tight text-stone-900 leading-snug">
                                {section.title[lang] || section.title.da}
                              </h2>
                            )}
                            {section.paragraphs && (
                              <div className="text-stone-700 text-base md:text-lg font-light leading-relaxed space-y-3 mb-4">
                                {section.paragraphs.map((p, pIdx) => (
                                  <p key={pIdx}>
                                    {p.type === "rich" ? renderRichText(p) : (p[lang] || p.da || p)}
                                  </p>
                                ))}
                              </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {section.cards.map((card, cIdx) => (
                                <div
                                  key={cIdx}
                                  className="p-7 md:p-8 bg-stone-50/70 hover:bg-stone-50 border border-stone-200 transition-all duration-300 flex flex-col justify-between group"
                                >
                                  <div>
                                    <div className="flex items-center justify-between mb-4">
                                      <span className="text-[10px] uppercase tracking-[0.3em] font-mono text-stone-500 font-medium">
                                        {card.tag?.[lang] || card.tag?.da}
                                      </span>
                                      <span className="text-xs font-mono text-stone-400 group-hover:text-stone-900 transition-colors">
                                        0{cIdx + 1}
                                      </span>
                                    </div>
                                    <h3 className="text-lg md:text-xl font-normal uppercase tracking-tight text-stone-900 mb-4 leading-snug">
                                      {card.title[lang] || card.title.da}
                                    </h3>
                                    <div className="text-stone-600 text-sm font-light leading-relaxed mb-6 space-y-3">
                                      {card.paragraphs ? (
                                        card.paragraphs.map((p, pIdx) => (
                                          <p key={pIdx}>
                                            {p.type === "rich" ? renderRichText(p) : (p[lang] || p.da || p)}
                                          </p>
                                        ))
                                      ) : (
                                        <p>
                                          {card.intro?.type === "rich" ? renderRichText(card.intro) : (card.intro?.[lang] || card.intro?.da || card.intro)}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  {card.bullets && (
                                    <ul className="space-y-2.5 pt-5 border-t border-stone-200/80">
                                      {(card.bullets[lang] || card.bullets.da).map((b, bIdx) => (
                                        <li key={bIdx} className="flex items-start gap-2.5 text-xs text-stone-700 font-light leading-relaxed">
                                          <CheckCircle2 className="w-3.5 h-3.5 text-stone-900 shrink-0 mt-0.5" />
                                          <span>
                                            {typeof b === "string" && b.includes("{link:")
                                              ? renderRichText(b)
                                              : (typeof b === "object" && b.type === "rich"
                                                ? renderRichText(b)
                                                : (typeof b === "object" ? (b[lang] || b.da) : b))}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }

                      if (section.type === "callout") {
                        return (
                          <div key={section.id || sIdx} className="p-8 md:p-12 bg-stone-900 text-stone-100 border border-stone-800 my-10 relative overflow-hidden shadow-sm">
                            <div className="flex items-center justify-between gap-4 mb-4">
                              <span className="text-[10px] uppercase tracking-[0.35em] font-mono text-stone-400 block">
                                {lang === "da" ? "Uforpligtende Gennemgang" : "No-Obligation Review"}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                <span className="text-[10px] uppercase tracking-widest font-mono text-stone-400">
                                  {lang === "da" ? "Klar til opstart" : "Available"}
                                </span>
                              </div>
                            </div>
                            <h2 className="text-2xl md:text-3xl font-light uppercase tracking-tight text-white mb-4 leading-snug">
                              {section.title[lang] || section.title.da}
                            </h2>
                            <div className="space-y-4 text-stone-300 text-sm md:text-base font-light leading-relaxed mb-8">
                              {section.paragraphs.map((p, pIdx) => (
                                <p key={pIdx}>
                                  {p.type === "rich" ? renderRichText(p, true) : (p[lang] || p.da || p)}
                                </p>
                              ))}
                            </div>
                            <Link
                              to={`${contactPath}?service=${encodeURIComponent(title)}`}
                              className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-stone-900 bg-white hover:bg-stone-100 px-7 py-4 font-mono font-semibold transition-all shadow-sm"
                            >
                              <span>{lang === "da" ? "Indhent tilbud" : "Request a quote"}</span>
                              <ArrowRight className="w-4 h-4" />
                            </Link>
                          </div>
                        );
                      }

                      // Customized Section 1: "early-clarification"
                      if (section.id === "early-clarification") {
                        return (
                          <div key={section.id} className="space-y-5 pt-2">
                            <div className="flex items-center gap-2 text-stone-400 font-mono text-xs uppercase tracking-[0.3em]">
                              <span className="w-1.5 h-1.5 rounded-full bg-stone-900" />
                              <span>01 / {lang === "da" ? "Tidlig Dialog & Omfang" : "Early Scoping"}</span>
                            </div>
                            <h2 className="text-2xl md:text-3xl font-light uppercase tracking-tight text-stone-900 leading-snug">
                              {section.title[lang] || section.title.da}
                            </h2>
                            <div className="text-stone-700 text-base md:text-lg font-light leading-relaxed">
                              <p>
                                {section.paragraphs[0]?.type === "rich" 
                                  ? renderRichText(section.paragraphs[0]) 
                                  : (section.paragraphs[0]?.[lang] || section.paragraphs[0]?.da || section.paragraphs[0])}
                              </p>
                            </div>
                            {section.paragraphs[1] && (
                              <div className="p-6 md:p-7 bg-stone-50 border-l-2 border-stone-900 text-stone-800 text-sm md:text-base font-light leading-relaxed mt-4">
                                <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-stone-500 block mb-2 font-medium">
                                  {lang === "da" ? "Faglig Anbefaling" : "Strategic Advice"}
                                </span>
                                <div className="italic text-stone-800">
                                  {section.paragraphs[1]?.type === "rich" 
                                    ? renderRichText(section.paragraphs[1]) 
                                    : (section.paragraphs[1]?.[lang] || section.paragraphs[1]?.da || section.paragraphs[1])}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }

                      // Customized Section 2: "structured-process"
                      if (section.id === "structured-process") {
                        return (
                          <div key={section.id} className="space-y-5 pt-4 border-t border-stone-200/80">
                            <div className="flex items-center gap-2 text-stone-400 font-mono text-xs uppercase tracking-[0.3em]">
                              <span className="w-1.5 h-1.5 rounded-full bg-stone-900" />
                              <span>02 / {lang === "da" ? "Gennemarbejdet Proces" : "Structured Process"}</span>
                            </div>
                            <h2 className="text-2xl md:text-3xl font-light uppercase tracking-tight text-stone-900 leading-snug">
                              {section.title[lang] || section.title.da}
                            </h2>
                            <div className="p-6 md:p-8 bg-white border border-stone-200/80 shadow-xs">
                              <p className="text-stone-700 text-base md:text-lg font-light leading-relaxed mb-6">
                                {section.paragraphs[0]?.type === "rich" 
                                  ? renderRichText(section.paragraphs[0]) 
                                  : (section.paragraphs[0]?.[lang] || section.paragraphs[0]?.da || section.paragraphs[0])}
                              </p>
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-5 border-t border-stone-100 text-xs font-mono">
                                <div className="space-y-1">
                                  <span className="text-stone-400 block">{lang === "da" ? "Bygherre & Rådgivere" : "Client & Advisors"}</span>
                                  <span className="text-stone-900 font-medium block">{lang === "da" ? "Klar dialog" : "Direct dialogue"}</span>
                                </div>
                                <div className="space-y-1">
                                  <span className="text-stone-400 block">{lang === "da" ? "Udførende Faggrupper" : "Execution Trades"}</span>
                                  <span className="text-stone-900 font-medium block">{lang === "da" ? "Samlet styring" : "Single coordination"}</span>
                                </div>
                                <div className="space-y-1">
                                  <span className="text-stone-400 block">{lang === "da" ? "Tidsplan & Rammer" : "Timeline & Scope"}</span>
                                  <span className="text-stone-900 font-medium block">{lang === "da" ? "Faste milepæle" : "Fixed milestones"}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // Customized Section 4: "handover-and-completion"
                      if (section.id === "handover-and-completion") {
                        return (
                          <div key={section.id} className="space-y-5 pt-4 border-t border-stone-200/80">
                            <div className="flex items-center gap-2 text-stone-400 font-mono text-xs uppercase tracking-[0.3em]">
                              <span className="w-1.5 h-1.5 rounded-full bg-stone-900" />
                              <span>04 / {lang === "da" ? "Afslutning & Kontrol" : "Completion & Handover"}</span>
                            </div>
                            <div className="p-7 md:p-8 bg-stone-50 border border-stone-200/90 relative overflow-hidden">
                              <div className="flex items-start justify-between gap-4 mb-4">
                                <h3 className="text-xl md:text-2xl font-light uppercase tracking-tight text-stone-900 leading-snug">
                                  {section.title[lang] || section.title.da}
                                </h3>
                                <div className="w-10 h-10 rounded-full border border-stone-200 bg-white flex items-center justify-center text-stone-700 shrink-0" aria-hidden="true">
                                  <ShieldCheck className="w-4 h-4" />
                                </div>
                              </div>
                              <div className="space-y-4 text-stone-700 text-base md:text-lg font-light leading-relaxed">
                                {section.paragraphs.map((p, pIdx) => (
                                  <p key={pIdx}>
                                    {p.type === "rich" ? renderRichText(p) : (p[lang] || p.da || p)}
                                  </p>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // Customized Section 5: "nationwide-coverage"
                      if (section.id === "nationwide-coverage" || section.type === "nationwide-coverage" || section.id === "geographic-scope") {
                        const badgesList = section.badges?.[lang] || section.badges?.da || section.badges || [
                          lang === "da" ? "Erhvervsbyggeri" : "Commercial",
                          lang === "da" ? "Nybyggeri" : "New builds",
                          lang === "da" ? "Renovering & Ombygning" : "Renovation",
                          lang === "da" ? "Kontorer & Erhvervslokaler" : "Offices & Retail",
                          lang === "da" ? "Pavilloner & Logistik" : "Pavilions & Logistics",
                          lang === "da" ? "Specialkonstruktioner" : "Custom Structures"
                        ];

                        return (
                          <div key={section.id} className="space-y-5 pt-4 border-t border-stone-200/80">
                            <div className="flex items-center gap-2 text-stone-400 font-mono text-xs uppercase tracking-[0.3em]">
                              <span className="w-1.5 h-1.5 rounded-full bg-stone-900" />
                              <span>{section.tag?.[lang] || section.tag?.da || (lang === "da" ? "05 / Kapacitet & Projekttyper" : "05 / Scope & Capacity")}</span>
                            </div>
                            <h2 className="text-2xl md:text-3xl font-light uppercase tracking-tight text-stone-900 leading-snug">
                              {section.title[lang] || section.title.da}
                            </h2>
                            <div className="space-y-4 text-stone-700 text-base md:text-lg font-light leading-relaxed">
                              {section.paragraphs.map((p, pIdx) => (
                                <p key={pIdx}>
                                  {p.type === "rich" ? renderRichText(p) : (p[lang] || p.da || p)}
                                </p>
                              ))}
                            </div>

                            {/* Architectural tags and coverage badges */}
                            <div className="pt-6 border-t border-stone-200 space-y-4">
                              <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-stone-400 block">
                                {lang === "da" ? "Geografi, Kompetencer & Opgavetyper" : "Coverage, Capabilities & Project Types"}
                              </span>
                              <div className="flex flex-wrap gap-2">
                                {badgesList.map((badge, bIdx) => (
                                  <span
                                    key={bIdx}
                                    className="text-xs px-3 py-1.5 bg-stone-100 text-stone-800 border border-stone-200 font-mono"
                                  >
                                    {badge}
                                  </span>
                                ))}
                              </div>
                              <div className="flex items-center gap-2 text-xs font-mono text-stone-500 pt-2">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse inline-block" />
                                <span>
                                  {lang === "da" 
                                    ? "Aktiv i København, Sjælland, Nordsjælland og hele Danmark" 
                                    : "Executing across Copenhagen, North Zealand and nationwide"}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // Type: intro-callout (e.g., holistic-understanding)
                      if (section.type === "intro-callout") {
                        return (
                          <div key={section.id || sIdx} className="space-y-5 pt-2">
                            <div className="flex items-center gap-2 text-stone-400 font-mono text-xs uppercase tracking-[0.3em]">
                              <span className="w-1.5 h-1.5 rounded-full bg-stone-900" />
                              <span>{section.tag?.[lang] || section.tag?.da || `0${sIdx + 1} / Faglig Tilgang`}</span>
                            </div>
                            <h2 className="text-2xl md:text-3xl font-light uppercase tracking-tight text-stone-900 leading-snug">
                              {section.title[lang] || section.title.da}
                            </h2>
                            <div className="text-stone-700 text-base md:text-lg font-light leading-relaxed space-y-4">
                              {section.paragraphs?.map((p, pIdx) => (
                                <p key={pIdx}>
                                  {p.type === "rich" ? renderRichText(p) : (p[lang] || p.da || p)}
                                </p>
                              ))}
                            </div>

                            {section.calloutBox && (
                              <div className="p-6 md:p-7 bg-stone-50 border-l-2 border-stone-900 text-stone-800 text-sm md:text-base font-light leading-relaxed mt-4">
                                <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-stone-500 block mb-2 font-medium">
                                  {section.calloutBox.tag?.[lang] || section.calloutBox.tag?.da}
                                </span>
                                <div className="italic text-stone-800 font-light">
                                  "{section.calloutBox.text?.[lang] || section.calloutBox.text?.da}"
                                </div>
                              </div>
                            )}

                            {section.stats && (
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-5 border-t border-stone-200/80 text-xs font-mono mt-6">
                                {section.stats.map((stat, stIdx) => (
                                  <div key={stIdx} className="space-y-1">
                                    <span className="text-stone-400 block">{stat.label?.[lang] || stat.label?.da}</span>
                                    <span className="text-stone-900 font-medium block">{stat.value?.[lang] || stat.value?.da}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      }

                      // Type: feature-grid (e.g., carpentry-scope 2x2 grid)
                      if (section.type === "feature-grid") {
                        return (
                          <div key={section.id || sIdx} className="space-y-6 pt-6 border-t border-stone-200/80">
                            <div className="flex items-center gap-2 text-stone-400 font-mono text-xs uppercase tracking-[0.3em]">
                              <span className="w-1.5 h-1.5 rounded-full bg-stone-900" />
                              <span>{section.tag?.[lang] || section.tag?.da || `0${sIdx + 1} / Fagområder`}</span>
                            </div>
                            <h2 className="text-2xl md:text-3xl font-light uppercase tracking-tight text-stone-900 leading-snug">
                              {section.title[lang] || section.title.da}
                            </h2>
                            {section.paragraphs && (
                              <div className="text-stone-700 text-base md:text-lg font-light leading-relaxed space-y-4">
                                {section.paragraphs.map((p, pIdx) => (
                                  <p key={pIdx}>
                                    {p.type === "rich" ? renderRichText(p) : (p[lang] || p.da || p)}
                                  </p>
                                ))}
                              </div>
                            )}

                            {section.cards && (
                              <div className={`grid grid-cols-1 ${section.cards.length === 3 ? "md:grid-cols-3" : "sm:grid-cols-2"} gap-5 pt-2`}>
                                {section.cards.map((card, cIdx) => (
                                  <div
                                    key={cIdx}
                                    className="p-6 md:p-7 bg-white border border-stone-200 transition-all duration-300 hover:border-stone-400 hover:shadow-xs flex flex-col justify-between group"
                                  >
                                    <div>
                                      <div className="flex items-center justify-between mb-3 text-stone-400 font-mono text-xs">
                                        <span className="text-[10px] uppercase tracking-[0.25em] text-stone-500 font-medium">
                                          {card.tag?.[lang] || card.tag?.da || (lang === "da" ? "Fagområde" : "Scope")}
                                        </span>
                                        <span className="text-stone-400 group-hover:text-stone-900 transition-colors">
                                          {card.number || `0${cIdx + 1}`}
                                        </span>
                                      </div>
                                      <h3 className="text-base md:text-lg font-normal uppercase tracking-tight text-stone-900 mb-2 leading-snug">
                                        {card.title[lang] || card.title.da}
                                      </h3>
                                      <p className="text-stone-600 text-sm font-light leading-relaxed mb-4">
                                        {card.description[lang] || card.description.da}
                                      </p>
                                    </div>
                                    {card.bullet && (
                                      <div className="pt-3 border-t border-stone-100 flex items-start gap-2 text-xs text-stone-700 font-light">
                                        <CheckCircle2 className="w-3.5 h-3.5 text-stone-900 shrink-0 mt-0.5" />
                                        <span>{card.bullet[lang] || card.bullet.da}</span>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      }

                      // Type: architectural-box (e.g., specialtømrerarbejde)
                      if (section.type === "architectural-box") {
                        const badgesList = section.badges?.[lang] || section.badges?.da || section.badges || [];
                        return (
                          <div key={section.id || sIdx} className="space-y-5 pt-6 border-t border-stone-200/80">
                            <div className="flex items-center gap-2 text-stone-400 font-mono text-xs uppercase tracking-[0.3em]">
                              <span className="w-1.5 h-1.5 rounded-full bg-stone-900" />
                              <span>{section.tag?.[lang] || section.tag?.da || `0${sIdx + 1} / Specialløsninger`}</span>
                            </div>
                            <h2 className="text-2xl md:text-3xl font-light uppercase tracking-tight text-stone-900 leading-snug">
                              {section.title[lang] || section.title.da}
                            </h2>
                            <div className="text-stone-700 text-base md:text-lg font-light leading-relaxed space-y-4">
                              {section.paragraphs?.map((p, pIdx) => (
                                <p key={pIdx}>
                                  {p.type === "rich" ? renderRichText(p) : (p[lang] || p.da || p)}
                                </p>
                              ))}
                            </div>

                            {badgesList.length > 0 && (
                               <div className="p-6 md:p-7 bg-stone-50/80 border border-stone-200/90 space-y-3">
                                 <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-stone-500 block">
                                   {section.badgesLabel?.[lang] || section.badgesLabel?.da || (lang === "da" ? "Særlige løsninger & udførelser" : "Custom executions & capabilities")}
                                 </span>
                                <div className="flex flex-wrap gap-2">
                                  {badgesList.map((badge, bIdx) => (
                                    <span
                                      key={bIdx}
                                      className="text-xs px-3 py-1.5 bg-white text-stone-800 border border-stone-200 font-mono shadow-2xs"
                                    >
                                      {badge}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }

                      // Type: materials-box (e.g., snedkerarbejde og specialinventar)
                      if (section.type === "materials-box") {
                        const matItems = section.materials?.items?.[lang] || section.materials?.items?.da || [];
                        const matLabel = section.materials?.label?.[lang] || section.materials?.label?.da || (lang === "da" ? "Udvælgelse af materialer" : "Material curation");

                        return (
                          <div key={section.id || sIdx} className="space-y-5 pt-6 border-t border-stone-200/80">
                            <div className="flex items-center gap-2 text-stone-400 font-mono text-xs uppercase tracking-[0.3em]">
                              <span className="w-1.5 h-1.5 rounded-full bg-stone-900" />
                              <span>{section.tag?.[lang] || section.tag?.da || `0${sIdx + 1} / Snedkeri`}</span>
                            </div>
                            <h2 className="text-2xl md:text-3xl font-light uppercase tracking-tight text-stone-900 leading-snug">
                              {section.title[lang] || section.title.da}
                            </h2>
                            <div className="text-stone-700 text-base md:text-lg font-light leading-relaxed space-y-4">
                              {section.paragraphs?.map((p, pIdx) => (
                                <p key={pIdx}>
                                  {p.type === "rich" ? renderRichText(p) : (p[lang] || p.da || p)}
                                </p>
                              ))}
                            </div>

                            {matItems.length > 0 && (
                              <div className="p-6 md:p-8 bg-stone-900 text-stone-100 border border-stone-800 space-y-4">
                                <div className="flex items-center justify-between border-b border-stone-800 pb-3">
                                  <span className="text-[10px] uppercase tracking-[0.3em] font-mono text-stone-400">
                                    {matLabel}
                                  </span>
                                  <span className="text-xs font-mono text-stone-400">CPH Collective</span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                                  {matItems.map((item, mIdx) => (
                                    <div
                                      key={mIdx}
                                      className="flex items-center gap-3 p-3 bg-stone-800/80 border border-stone-700/80 text-xs md:text-sm text-stone-200 font-light"
                                    >
                                      <span className="w-1.5 h-1.5 rounded-full bg-stone-400 shrink-0" />
                                      <span>{item}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }

                      // Type: checklist (e.g., montagearbejde)
                      if (section.type === "checklist") {
                        const bulletsList = section.bullets?.[lang] || section.bullets?.da || [];

                        return (
                          <div key={section.id || sIdx} className="space-y-5 pt-6 border-t border-stone-200/80">
                            <div className="flex items-center gap-2 text-stone-400 font-mono text-xs uppercase tracking-[0.3em]">
                              <span className="w-1.5 h-1.5 rounded-full bg-stone-900" />
                              <span>{section.tag?.[lang] || section.tag?.da || `0${sIdx + 1} / Montage`}</span>
                            </div>
                            <h2 className="text-2xl md:text-3xl font-light uppercase tracking-tight text-stone-900 leading-snug">
                              {section.title[lang] || section.title.da}
                            </h2>
                            <div className="text-stone-700 text-base md:text-lg font-light leading-relaxed space-y-4">
                              {section.paragraphs?.map((p, pIdx) => (
                                <p key={pIdx}>
                                  {p.type === "rich" ? renderRichText(p) : (p[lang] || p.da || p)}
                                </p>
                              ))}
                            </div>

                            {bulletsList.length > 0 && (
                             <div className="p-6 md:p-8 bg-stone-50 border border-stone-200 space-y-4">
                               <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-stone-500 block">
                                 {section.bulletsLabel?.[lang] || section.bulletsLabel?.da || (lang === "da" ? "Faste procedurer for montage og pladskoordinering" : "Installation & site coordination procedure")}
                               </span>
                                <ul className="space-y-3">
                                  {bulletsList.map((bullet, bIdx) => (
                                    <li
                                      key={bIdx}
                                      className="flex items-start gap-3 text-sm md:text-base text-stone-800 font-light leading-relaxed"
                                    >
                                      <div className="w-6 h-6 rounded-full bg-white border border-stone-300 flex items-center justify-center shrink-0 mt-0.5">
                                        <span className="text-xs font-mono font-medium text-stone-900">{bIdx + 1}</span>
                                      </div>
                                      <span className="pt-0.5">
                                        {typeof bullet === "string" && bullet.includes("{link:")
                                          ? renderRichText(bullet)
                                          : (typeof bullet === "object" && bullet.type === "rich"
                                            ? renderRichText(bullet)
                                            : (typeof bullet === "object" ? (bullet[lang] || bullet.da) : bullet))}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        );
                      }

                      // Fallback for any other custom section
                      return (
                        <div key={section.id || sIdx} className="space-y-4 pt-2">
                          {section.level === "h2" ? (
                            <h2 className="text-2xl md:text-3xl font-light uppercase tracking-tight text-stone-900">
                              {section.title[lang] || section.title.da}
                            </h2>
                          ) : (
                            <h3 className="text-xl md:text-2xl font-light uppercase tracking-tight text-stone-900">
                              {section.title[lang] || section.title.da}
                            </h3>
                          )}
                          <div className="space-y-4 text-stone-700 text-base md:text-lg font-light leading-relaxed">
                            {section.paragraphs.map((p, pIdx) => (
                              <p key={pIdx}>
                                {p.type === "rich" ? renderRichText(p) : (p[lang] || p.da || p)}
                              </p>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    <h2 className="text-3xl md:text-4xl font-light uppercase tracking-tight text-stone-900 mb-8">
                      {lang === "da" ? `Om vores ${title.toLowerCase()}` : `About our ${title.toLowerCase()}`}
                    </h2>

                    <div className="space-y-6 text-stone-700 text-base md:text-lg font-light leading-relaxed">
                      {longParagraphs.map((para, i) => (
                        <p key={i}>{linkFirstCphCollective(para, false)}</p>
                      ))}
                    </div>

                    {/* Quote / Ideal For Box */}
                    {idealFor && (
                      <div className="mt-10 p-6 md:p-8 bg-stone-100 border-l-2 border-stone-900">
                        <span className="text-[11px] uppercase tracking-[0.25em] text-stone-500 font-mono block mb-2">
                          {lang === "da" ? "Hvem henvender ydelsen sig til?" : "Ideal Target Audience"}
                        </span>
                        <p className="text-stone-800 text-sm md:text-base font-light italic leading-relaxed">
                          "{idealFor}"
                        </p>
                      </div>
                    )}
                  </>
                )}
              </FadeIn>
            </div>

            {/* Right: Secondary Visual & Scope List */}
            <div className="lg:col-span-5 space-y-12 lg:sticky lg:top-28">
              {service.secondaryImage && (
                <FadeIn direction="up" delay={100}>
                  <div className="relative h-[340px] md:h-[420px] overflow-hidden border border-stone-200">
                    <img
                      src={getOptimizedImage(service.secondaryImage, 900)}
                      alt={`${title} – Detaljer og udførelse | CPH Collective`}
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                </FadeIn>
              )}

              {/* Scope of Work */}
              <FadeIn direction="up" delay={200}>
                <div className="border border-stone-200 bg-white p-8 md:p-10">
                  <span className="text-xs uppercase tracking-[0.3em] text-stone-400 font-mono block mb-3">
                    {lang === "da" ? "Ydelsesomfang" : "Scope of Deliverables"}
                  </span>
                  <h3 className="text-xl md:text-2xl font-light uppercase tracking-tight text-stone-900 mb-6">
                    {lang === "da" ? "Hvad er inkluderet?" : "What's included?"}
                  </h3>
                  <ul className="space-y-3.5">
                    {includes.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-stone-700 font-light">
                        <CheckCircle2 className="w-4 h-4 text-stone-900 shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* 4-Step Process Section */}
      {processSteps.length > 0 && (
        <section className="py-24 md:py-32 bg-stone-100 border-b border-stone-200">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <FadeIn direction="up">
              <div className="max-w-2xl mb-16 md:mb-20">
                <span className="text-xs uppercase tracking-[0.35em] text-stone-500 font-mono block mb-3">
                  {lang === "da" ? "Struktureret Arbejdsgang" : "Workflow & Phases"}
                </span>
                <h2 className="text-3xl md:text-5xl font-light uppercase tracking-tight text-stone-900 mb-4">
                  {lang === "da" ? "Sådan forløber processen" : "How the process unfolds"}
                </h2>
                <p className="text-stone-600 font-light text-base md:text-lg">
                  {lang === "da"
                    ? "Fra første møde til endelig aflevering arbejder vi efter faste kvalitetsprocedurer."
                    : "From the initial consultation to final handover, we work according to rigorous quality protocols."}
                </p>
              </div>
            </FadeIn>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {processSteps.map((step, idx) => {
                const stepTitle = step.title[lang] || step.title.da;
                const stepText = step.text[lang] || step.text.da;
                return (
                  <FadeIn key={idx} direction="up" delay={idx * 100}>
                    <div className="h-full flex flex-col justify-between p-8 bg-white border border-stone-200 transition-shadow duration-300 hover:shadow-md">
                      <div>
                        <span className="text-3xl font-light text-stone-300 font-mono block mb-4">
                          {step.step}
                        </span>
                        <h3 className="text-lg font-medium text-stone-900 uppercase tracking-tight mb-3">
                          {stepTitle}
                        </h3>
                        <p className="text-sm text-stone-600 font-light leading-relaxed">
                          {stepText}
                        </p>
                      </div>
                      <div className="mt-8 pt-4 border-t border-stone-100 flex items-center justify-between text-[11px] font-mono text-stone-400 uppercase tracking-wider">
                        <span>Fase 0{idx + 1}</span>
                        <CheckCircle2 className="w-3.5 h-3.5 text-stone-400" />
                      </div>
                    </div>
                  </FadeIn>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Benefits / Value Proposition */}
      {benefits.length > 0 && (
        <section className="py-24 md:py-32 border-b border-stone-200">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
              <div className="lg:col-span-4">
                <FadeIn direction="up">
                  <span className="text-xs uppercase tracking-[0.35em] text-stone-400 font-mono block mb-3">
                    {lang === "da" ? "Tryghed & Kvalitet" : "Client Assurance"}
                  </span>
                  <h2 className="text-3xl md:text-4xl font-light uppercase tracking-tight text-stone-900 mb-6">
                    {lang === "da" ? "Hvorfor vælge CPH Collective?" : "Why choose CPH Collective?"}
                  </h2>
                  <p className="text-stone-600 font-light text-base leading-relaxed mb-8">
                    {lang === "da"
                      ? "Vi forstår kompleksiteten i moderne byggeri og leverer et samarbejde bygget på gennemsigtighed, faglig stolthed og levering til tiden."
                      : "We understand the demands of modern construction and offer collaboration built on transparency, craft pride, and reliable timing."}
                  </p>
                  <Link
                    to={contactPath}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-stone-900 border-b border-stone-900 pb-1 hover:text-stone-600 font-mono transition-colors"
                  >
                    <span>{lang === "da" ? "Start dialogen nu" : "Start the conversation"}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </FadeIn>
              </div>

              <div className="lg:col-span-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {benefits.map((b, i) => {
                    const bTitle = b.title[lang] || b.title.da;
                    const bText = b.text[lang] || b.text.da;
                    return (
                      <FadeIn key={i} direction="up" delay={i * 120}>
                        <div className="p-6 md:p-8 bg-stone-50 border border-stone-200 h-full flex flex-col justify-between">
                          <div>
                            <div className="w-8 h-8 rounded-full border border-stone-300 flex items-center justify-center text-stone-700 mb-6">
                              <span className="text-xs font-mono">0{i + 1}</span>
                            </div>
                            <h3 className="text-base font-medium text-stone-900 uppercase tracking-tight mb-3">
                              {bTitle}
                            </h3>
                            <p className="text-sm text-stone-600 font-light leading-relaxed">
                              {bText}
                            </p>
                          </div>
                        </div>
                      </FadeIn>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Frequently Asked Questions (FAQ) Section */}
      {faqs.length > 0 && (
        <section className="py-24 md:py-32 bg-stone-100 border-b border-stone-200">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="max-w-4xl mx-auto">
              <FadeIn direction="up">
                <div className="text-center mb-16">
                  <span className="text-xs uppercase tracking-[0.35em] text-stone-500 font-mono block mb-3">
                    {lang === "da" ? "FAQ & Afklaring" : "Frequently Asked Questions"}
                  </span>
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-light uppercase tracking-tight text-stone-900 mb-6">
                    {lang === "da" 
                      ? `Ofte stillede spørgsmål om ${title.toLowerCase()}` 
                      : `Frequently asked questions about ${title.toLowerCase()}`}
                  </h2>
                  <p className="text-stone-600 font-light text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
                    {lang === "da"
                      ? `Her finder I svar på 5 af de mest centrale spørgsmål om planlægning, økonomi, samarbejde og udførelse ved ${title.toLowerCase()} hos CPH Collective.`
                      : `Find answers to 5 essential questions regarding planning, budgets, collaboration, and execution for ${title.toLowerCase()} with CPH Collective.`}
                  </p>
                </div>
              </FadeIn>

              {/* FAQ Accordion List */}
              <div className="space-y-4">
                {faqs.map((faqItem, idx) => {
                  const question = faqItem.q[lang] || faqItem.q.da;
                  const answer = faqItem.a[lang] || faqItem.a.da;
                  const isOpen = openFaqIndex === idx;

                  return (
                    <FadeIn key={idx} direction="up" delay={idx * 60}>
                      <div className="border border-stone-300 bg-white transition-all duration-200 shadow-xs">
                        <button
                          type="button"
                          onClick={() => setOpenFaqIndex(isOpen ? -1 : idx)}
                          aria-expanded={isOpen}
                          className="w-full py-6 px-6 md:px-8 text-left flex items-center justify-between gap-4 group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900"
                        >
                          <div className="flex items-center gap-4 md:gap-6 pr-2">
                            <span className="text-xs font-mono text-stone-400 group-hover:text-stone-900 transition-colors shrink-0">
                              0{idx + 1}
                            </span>
                            <span className="text-base md:text-lg font-medium text-stone-900 tracking-tight">
                              {question}
                            </span>
                          </div>
                          <div className="w-8 h-8 rounded-full border border-stone-200 flex items-center justify-center text-stone-600 shrink-0 group-hover:border-stone-900 group-hover:text-stone-900 transition-all duration-200 bg-stone-50">
                            {isOpen ? (
                              <Minus className="w-4 h-4" />
                            ) : (
                              <Plus className="w-4 h-4" />
                            )}
                          </div>
                        </button>

                        {isOpen && (
                          <div className="px-6 md:px-8 pb-6 pt-1 border-t border-stone-100 text-stone-600 font-light text-base leading-relaxed">
                            <div className="pl-7 md:pl-10 text-stone-700">
                              {linkFirstCphCollective(answer, false)}
                            </div>
                          </div>
                        )}
                      </div>
                    </FadeIn>
                  );
                })}
              </div>

              {/* Schema.org FAQPage structured data */}
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    "mainEntity": faqs.map((f) => ({
                      "@type": "Question",
                      "name": f.q[lang] || f.q.da,
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": f.a[lang] || f.a.da
                      }
                    }))
                  })
                }}
              />
            </div>
          </div>
        </section>
      )}

      {/* SEO Keywords Tag Cloud */}
      {seoKeywords.length > 0 && (
        <section className="py-12 border-b border-stone-200 bg-stone-50">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <span className="text-xs uppercase tracking-[0.25em] text-stone-400 font-mono shrink-0">
                {lang === "da" ? "Faglige Søgeord & Kompetencer:" : "Core Competencies & Keywords:"}
              </span>
              <div className="flex flex-wrap gap-2">
                {seoKeywords.map((kw, i) => (
                  <span
                    key={i}
                    className="text-[11px] font-mono text-stone-600 border border-stone-200 px-3 py-1 bg-stone-100"
                  >
                    #{kw}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA Block */}
      <section className="bg-stone-900 text-stone-100 py-24 md:py-32">
        <div className="w-full px-6 md:px-12 lg:px-16 text-center">
          <div className="max-w-3xl mx-auto">
            <FadeIn direction="up">
              <span className="text-xs uppercase tracking-[0.4em] text-stone-400 font-mono block mb-4">
                {lang === "da" ? "Uforpligtende Gennemgang" : "No-obligation Consultation"}
              </span>
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-light uppercase tracking-tight text-white mb-6">
                {lang === "da"
                  ? `Klar til at få dit projekt med ${title.toLowerCase()} videre?`
                  : `Ready to move your ${title.toLowerCase()} project forward?`}
              </h2>
              <p className="text-stone-300 text-base md:text-lg font-light mb-10 leading-relaxed max-w-2xl mx-auto">
                {lang === "da"
                  ? "Kontakt CPH Collective for en professionel gennemgang af tegninger, tidsplan og budget. Vi rådgiver uforpligtende."
                  : "Contact CPH Collective for a professional review of drawings, timeline, and budget. We provide constructive advice without obligation."}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link
                  to={contactPath}
                  className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-stone-900 bg-white hover:bg-stone-100 transition-all duration-300 px-8 py-4 font-semibold font-mono"
                >
                  <span>{lang === "da" ? "Kontakt os i dag" : "Get in touch today"}</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to={lang === "da" ? "/projekter" : "/en/projects"}
                  className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-stone-300 hover:text-white border border-stone-700 hover:border-stone-500 transition-all duration-300 px-8 py-4 font-mono"
                >
                  <span>{lang === "da" ? "Se vores referencer" : "View references"}</span>
                </Link>
                <Link
                  to={lang === "da" ? "/om-os" : "/en/about"}
                  className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-stone-300 hover:text-white border border-stone-700 hover:border-stone-500 transition-all duration-300 px-8 py-4 font-mono"
                >
                  <span>{lang === "da" ? "Om CPH Collective" : "About CPH Collective"}</span>
                </Link>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Prev / Next Service Navigator Bar */}
      <section className="border-t border-stone-200 bg-stone-100 py-8">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Link
              to={prevPath}
              className="p-6 bg-white border border-stone-200 hover:border-stone-400 transition-colors flex items-center justify-between group"
            >
              <div>
                <span className="text-[11px] uppercase tracking-wider text-stone-400 font-mono block mb-1">
                  ← {lang === "da" ? "Forrige Ydelse" : "Previous Service"}
                </span>
                <span className="text-base font-medium text-stone-900 uppercase">
                  {prevService.title[lang] || prevService.title.da}
                </span>
              </div>
              <ArrowLeft className="w-4 h-4 text-stone-400 group-hover:-translate-x-1 transition-transform" />
            </Link>

            <Link
              to={nextPath}
              className="p-6 bg-white border border-stone-200 hover:border-stone-400 transition-colors flex items-center justify-between group text-right"
            >
              <ArrowRight className="w-4 h-4 text-stone-400 group-hover:translate-x-1 transition-transform order-last sm:order-first" />
              <div>
                <span className="text-[11px] uppercase tracking-wider text-stone-400 font-mono block mb-1">
                  {lang === "da" ? "Næste Ydelse" : "Next Service"} →
                </span>
                <span className="text-base font-medium text-stone-900 uppercase">
                  {nextService.title[lang] || nextService.title.da}
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
